import React, {Attributes, FunctionComponent, PropsWithChildren, useContext, useMemo} from "react";
import {createUseStyles, JssProvider} from "react-jss";
import {create as createJss} from "jss";
import preset from "jss-preset-default";
import {reactShadowRootContext} from "@infonova/framework/lib/webcomponents/context/reactShadowRootContext";
import {generateClassName} from "./generateClassName";

// @ts-ignore
import normalizeStyles from "!../../../../build/jss/loader?modules!modern-normalize/modern-normalize.css";

const globalStyles = {
    '@global': {

        // Resets

        ...normalizeStyles['@global'],
        'input::-webkit-search-cancel-button': {
            display: 'none'
        },
        'ol, ul': {
            listStyleType: 'none',
            margin: '0',
            padding: '0',
        },
        'h1, h2, h3, h4, h5, h6': {
            fontSize: 'inherit',
            fontWeight: 'inherit',
        },
        'blockquote, dl, dd, h1, h2, h3, h4, h5, h6, hr, figure, p, pre': {
            margin: '0',
        },

        // Utility classes

        '.is-selectable': {
            cursor: 'pointer'
        },
        '.is-moveable': {
            cursor: 'move'
        },
        '.is-erroneous': {},
        '[role=button]': {
            cursor: 'pointer'
        },
        '.overflow-hidden': {
            overflow: 'hidden',
        },
        '.min-width-0': {
            minWidth: 0
        },
        '.break-word': {
            wordBreak: 'break-word'
        },
        '.text-ellipsis': {
            textOverflow: 'ellipsis'
        },
        '.justify-start': {
            justifyContent: 'flex-start'
        },
        '.justify-center': {
            justifyContent: 'center'
        },
        '.justify-between': {
            justifyContent: 'space-between'
        },
        '.justify-around': {
            justifyContent: 'space-around'
        },
        '.justify-end': {
            justifyContent: 'flex-end'
        },
        '.items-start': {
            alignItems: 'flex-start'
        },
        '.items-center': {
            alignItems: 'center'
        },
        '.items-end': {
            alignItems: 'flex-end'
        },
        '.hidden': {
            display: 'none'
        }
    }
}

// MIN_SAFE_INTEGER => MIN_INTEGER + 1
const useGlobalStyles = createUseStyles(globalStyles, { index: Number.MIN_SAFE_INTEGER - 1});

const GlobalStyling = (props: PropsWithChildren<{}>) => {
    useGlobalStyles();
    return <>{props.children}</>;
};

export const JssWrapperComponent = (props: PropsWithChildren<{}>) => {
    const shadowRoot = useContext(reactShadowRootContext());
    const jssInstance = useMemo(() => {
        if (shadowRoot) {
            const node = document.createComment('CSS');
            shadowRoot.appendChild(node);
            return createJss({
                ...preset(),
                insertionPoint: node,
            });
        } else {
            return undefined;
        }
    }, [shadowRoot])
    return <>
        {jssInstance != null ? <JssProvider jss={jssInstance} generateId={generateClassName}>
            <GlobalStyling>
                {props.children}
            </GlobalStyling>
        </JssProvider> : null}
    </>;
};

export const JssWrapper = <FC extends FunctionComponent<P>, P>(Component: FC): FunctionComponent<P> => {
    return (props: P) => {
        return <JssWrapperComponent>
            {React.createElement(Component as any as FunctionComponent<{}>, props as Attributes)}
        </JssWrapperComponent>;
    };
}
